"use client"

import { useState, useMemo } from "react"
import { Activity, Clock } from "lucide-react"
import JobsList from "./components/JobsList"
import JobHeader from "./components/JobHeader"
import MetricsCards from "./components/MetricsCards"
import TimeseriesChart from "./components/TimeseriesChart"
import LogsPanel from "./components/LogsPanel"
import useSubmissionsTimeseries from "./hooks/useSubmissionsTimeseries"
import useLastSubmission from "./hooks/useLastSubmission"
import { useIntl } from "react-intl"

export default function JobMonitoringDashboard({ jobs, viewJob, refreshToken }) {

  const [selectedJob, setSelectedJob] = useState({})
  const [activeTab, setActiveTab] = useState("submissions")
  const [sidebarOpen, setSidebarOpen] = useState(false)

  // NEW: range state and helpers
  const [rangeKey, setRangeKey] = useState("1D")

  // Memoize range so it only changes when rangeKey or selected job changes
  const { start, end } = useMemo(() => {
    const end = new Date()
    const start = new Date(end)
    if (rangeKey === "1D") start.setDate(end.getDate() - 1)
    else if (rangeKey === "1W") start.setDate(end.getDate() - 7)
    else if (rangeKey === "1M") start.setMonth(end.getMonth() - 1)
    else if (rangeKey === "1Y") start.setFullYear(end.getFullYear() - 1)
    return { start, end }
  }, [rangeKey])

  // Stable options object for the hook (pass Date objects; hook will toISOString)
  const tsOpts = useMemo(
    () => ({
      startTs: selectedJob?.id ? start : undefined,
      endTs: selectedJob?.id ? end : undefined,
      limit: 8640
    }),
    [selectedJob?.id, start, end]
  )

  // fetch only when a job is selected and within selected range
  const { series, loading, error } = useSubmissionsTimeseries(
    selectedJob,
    tsOpts,
    refreshToken
  )

  const {
    data: lastVerified,
    loading: lastVerifiedLoading,
    error: lastVerifiedError
  } = useLastSubmission({ jobId: selectedJob?.id, includePayload: false, refreshToken })

  const intl = useIntl()

  return (
    <div className="flex h-screen bg-gray-100 overflow-scroll flex-col sm:flex-row">

      <div className="hidden h-full overflow-scroll sm:!flex sm:flex-col shrink-0 overflow-y-auto border-r border-gray-200 bg-white">
        <JobsList
          viewJob={viewJob}
          jobs={jobs}
          selectedJob={selectedJob}
          onJobSelect={setSelectedJob}
          refreshToken={refreshToken}
        />
      </div>
      {sidebarOpen && (
        <div className="fixed inset-0 z-40">
          <div className="absolute inset-0 bg-black/30" onClick={() => setSidebarOpen(false)} />
          <div className="absolute inset-y-0 left-0 w-80 max-w-full bg-white shadow-xl overflow-y-auto">
            <div className="p-3 border-b flex items-center justify-between">
              <span className="text-sm font-medium">
                {intl.formatMessage({ id: "dga.jobs.title", defaultMessage: "Jobs" })}
              </span>
              <button
                className="px-2 py-1 text-sm rounded-md border bg-white"
                onClick={() => setSidebarOpen(false)}
              >
                {intl.formatMessage({ id: "common.close", defaultMessage: "Close" })}
              </button>
            </div>
            <JobsList
              viewJob={viewJob}
              jobs={jobs}
              selectedJob={selectedJob}
              onJobSelect={(job) => {
                setSelectedJob(job)
                setSidebarOpen(false)
              }}
              refreshToken={refreshToken}
            />
          </div>
        </div>
      )}

      {/* Main Content Area */}
      <div className="h-full overflow-scroll flex-1 min-w-0 flex flex-col">
        {/* Mobile top controls */}
        <div className="sm:!hidden p-2 border-b bg-white flex items-center justify-between">
          <button
            className="px-3 py-2 text-sm rounded-md border bg-white"
            onClick={() => setSidebarOpen(true)}
          >
            {selectedJob?.id
              ? intl.formatMessage({ id: "dga.jobs.changeJob", defaultMessage: "Change job" })
              : intl.formatMessage({ id: "dga.jobs.selectJob", defaultMessage: "Select job" })}
          </button>
        </div>

        <JobHeader
          selectedJob={selectedJob}
          // NEW: range controls
          activeRangeKey={rangeKey}
          onRangeChange={setRangeKey}
        />

        <div className="flex-1 p-4 md:p-6 mb-2">
          <div className="flex flex-col gap-2">
            {/* Custom Tab List */}
            <div className="overflow-x-auto">
              <div className="inline-flex h-9 w-fit items-center justify-center rounded-lg bg-gray-100 p-1">
                <button
                  onClick={() => setActiveTab("submissions")}
                  className={`inline-flex h-7 flex-1 items-center justify-center gap-2 rounded-md px-3 py-1 text-sm font-medium transition-colors ${activeTab === "submissions" ? "bg-white text-gray-900 shadow-sm" : "text-gray-600 hover:text-gray-900"
                    }`}
                >
                  <Activity className="h-4 w-4" />
                  {intl.formatMessage({ id: "dga.tabs.submissions", defaultMessage: "Submissions" })}
                </button>
                <button
                  onClick={() => setActiveTab("logs")}
                  className={`inline-flex h-7 flex-1 items-center justify-center gap-2 rounded-md px-3 py-1 text-sm font-medium transition-colors ${activeTab === "logs" ? "bg-white text-gray-900 shadow-sm" : "text-gray-600 hover:text-gray-900"
                    }`}
                >
                  <Clock className="h-4 w-4" />
                  {intl.formatMessage({ id: "dga.tabs.logs", defaultMessage: "Logs" })}
                </button>
              </div>
            </div>

            {/* Tab Content */}
            {activeTab === "submissions" && (
              <div className="flex-1 mt-4 md:mt-6 space-y-4 md:space-y-6">
                <MetricsCards
                  selectedJob={selectedJob}
                  lastVerified={lastVerified}
                  lastVerifiedLoading={lastVerifiedLoading}
                  lastVerifiedError={lastVerifiedError}
                />
                <TimeseriesChart
                  key={selectedJob?.id || 'nojob'} // remount only when job changes
                  selectedJob={selectedJob}
                  series={series}
                  loading={loading}
                  error={error}
                />
              </div>
            )}

            {activeTab === "logs" && (
              <div className="flex-1 mt-4 md:mt-6">
                <div>
                  <LogsPanel
                    selectedJob={selectedJob}
                    startTime={start}
                    endTime={end}
                    refreshToken={refreshToken}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
